// src/components/EventView.js

import React, { useEffect, useState } from 'react';
import api from '../api';
import {
  Typography,
  Button,
  List,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Alert,
  Paper,
  Box,
  Chip,
  Skeleton,
} from '@mui/material';
import { Add, Delete, Edit, Save, Cancel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoadingState from './LoadingState';
import PageHeader from './PageHeader';
import dayjs from 'dayjs';


function EventView() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/events');
      const data = response.data;

      if (response.status === 204 || !Array.isArray(data)) {
        setEvents([]);
      } else {
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleCreate = async () => {
    try {
      await api.post('/events', { name: newEventName, date: newEventDate });
      setNewEventName('');
      setNewEventDate(dayjs().format('YYYY-MM-DD'));
      setOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      setError('Failed to create event. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete event. Please try again.');
    }
  };

  const handleStartEdit = (event, e) => {
    e.stopPropagation();
    setEditingEvent(event._id);
    setEditName(event.name);
    setEditDate(dayjs(event.date).format('YYYY-MM-DD'));
  };

  const handleCancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingEvent(null);
    setEditName('');
    setEditDate('');
  };

  const handleSaveEdit = async (eventId, e) => {
    if (e) e.stopPropagation();
    try {
      await api.put(`/events/${eventId}`, {
        name: editName,
        date: editDate
      });
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event. Please try again.');
    }
  };

  const handleDoubleClick = (event, e) => {
    e.stopPropagation();
    handleStartEdit(event, e);
  };

  return (
    <div>
      <PageHeader
        title="My Events"
        variant="h4"
        mb={3}
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            Create New Event
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <LoadingState message="Loading your events..." minHeight={240}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={72} sx={{ mb: 1 }} />
          ))}
        </LoadingState>
      ) : (
        <>
          {Array.isArray(events) && events.length > 0 ? (
            <List>
              {events.map((event) => (
                <Paper
                  key={event._id}
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: editingEvent === event._id ? 'default' : 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    transition: 'all 0.2s ease',
                    '&:hover': editingEvent === event._id ? {} : {
                      backgroundColor: 'rgba(232,137,26,0.04)',
                      transform: 'translateX(4px)',
                    },
                  }}
                  onClick={editingEvent === event._id ? undefined : () => handleEventClick(event._id)}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box flex={1}>
                      {editingEvent === event._id ? (
                        <Box display="flex" flexDirection="column" gap={1} onClick={(e) => e.stopPropagation()}>
                          <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            fullWidth
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEdit(event._id, e);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit(e);
                              }
                            }}
                          />
                          <TextField
                            size="small"
                            type="date"
                            value={editDate}
                            onChange={(e) => setEditDate(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </Box>
                      ) : (
                        <Box onDoubleClick={(e) => handleDoubleClick(event, e)}>
                          <Typography variant="h6">{event.name}</Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: 'primary.dark', fontWeight: 600 }}
                          >
                            {dayjs(event.date).format('MMMM D, YYYY')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box display="flex" gap={1} alignItems="center">
                      {event.members.length > 0 && editingEvent !== event._id && (
                        <Box display="flex" alignItems="center">
                          {event.members.slice(0, 2).map((member) => (
                            <Chip
                              key={member._id}
                              label={member.email}
                              size="small"
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                          {event.members.length > 2 && (
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              +{event.members.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      )}
                      {editingEvent === event._id ? (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => handleSaveEdit(event._id, e)}
                          >
                            <Save />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => handleStartEdit(event, e)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={loading}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(event._id);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>
          ) : (
            <Typography>No events found.</Typography>
          )}
        </>
      )}

      {/* Dialog for creating a new event */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Event Name"
            type="text"
            fullWidth
            value={newEventName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreate();
              }
            }}
            onChange={(e) => setNewEventName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Event Date"
            type="date"
            fullWidth
            value={newEventDate}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setNewEventDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EventView;
